<template>
  <teleport to="#headerPageContent">
    <router-link
      v-if="isAdmin"
      #="{ navigate }"
      :to="{ name: 'Users/AddCompany' }"
      custom
    >
      <HeaderBarItem @click="navigate">
        <HeaderIconButton>
          <template #icon>
            <PlusCircleSvg />
          </template>

          <template #label>
            Add new company
          </template>
        </HeaderIconButton>
      </HeaderBarItem>
    </router-link>
    <router-link
      v-if="isAdmin || isCompanyAdmin"
      #="{ navigate }"
      :to="{ name: 'Users/Add' }"
      custom
    >
      <HeaderBarItem @click="navigate">
        <HeaderIconButton>
          <template #icon>
            <PlusCircleSvg />
          </template>

          <template #label>
            Add new user
          </template>
        </HeaderIconButton>
      </HeaderBarItem>
    </router-link>
  </teleport>

  <BaseContainer :class="$style.page">
    <BaseLoader
      is-block
      :is-active="$apollo.queries.users.loading || isPostProcessingUsers"
    >
      <TableSection
        :page="page"
        :data="users"
        :total-items="totalUsers"
        :per-page="perPage"
        :per-page-options="$options.PER_PAGE_OPTIONS"
        @update:page="setPage"
        @update:per-page="setPerPage"
      >
        <template #heading>
          All users
        </template>

        <UsersTableColumns @disable="disableUser" />
      </TableSection>
    </BaseLoader>
  </BaseContainer>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import gql from 'graphql-tag';
import BaseContainer from '@/components/base/Container';
import BaseLoader from '@/components/base/Loader';
import TableSection from '@/components/TableSection';
import UsersTableColumns from '@/components/UsersTableColumns';
import HeaderBarItem from '@/components/HeaderBarItem';
import HeaderIconButton from '@/components/HeaderIconButton';
import PlusCircleSvg from '@/assets/images/icons/plus-circle.svg?inline';
import { companyAdminQueries, adminQueries } from '@/aws-api';

function transformCognitoData(data) {
  return {
    givenName: data.UserAttributes.find((x) => x.Name === 'given_name').Value,
    familyName: data.UserAttributes.find((x) => x.Name === 'family_name').Value,
    email: data.UserAttributes.find((x) => x.Name === 'email').Value,
    groups: data.Groups,
  };
}

export default {
  components: {
    BaseContainer,
    BaseLoader,
    TableSection,
    UsersTableColumns,
    HeaderBarItem,
    HeaderIconButton,
    PlusCircleSvg,
  },
  PER_PAGE_OPTIONS: [10, 20, 30],
  apollo: {
    users: {
      query() {
        return this.isCompanyAdmin
          ? gql`
          query ($limit: Int!, $offset: Int!, $companyId: Int!) {
            users_aggregate(where: {
              suspended: {_eq: false},
              company_id: {_eq: $companyId},
            }) {
              aggregate {
                count
              }
            }

            users(
              limit: $limit,
              offset: $offset,
              order_by: {created_at: desc},
              where: {
                suspended: {_eq: false},
                company_id: {_eq: $companyId},
              }
            ) {
              company {
                id
                name
                owner_id
              }
              created_at
              id
            }
          }
        `
          : gql`
          query ($limit: Int!, $offset: Int!) {
            users_aggregate(where: {
              suspended: {_eq: false},
            }) {
              aggregate {
                count
              }
            }

            users(
              limit: $limit,
              offset: $offset,
              where: {
                suspended: {_eq: false},
              }
              order_by: {created_at: desc},
            ) {
              company {
                id
                name
                owner_id
              }
              created_at
              id
            }
          }
        `;
      },
      debounce: 250,
      variables() {
        return {
          limit: this.perPage,
          offset: this.perPage * (this.page - 1),
          companyId: this.user.company?.id,
        };
      },
      update(data) {
        this.totalUsers = data.users_aggregate.aggregate.count;

        return data.users;
      },
      manual: true,
      async result({ data }) {
        this.isPostProcessingUsers = true;
        const usernames = data.users.map((user) => user.id);

        const cognitoResult = await (this.isCompanyAdmin
          ? companyAdminQueries.getUsers(usernames)
          : adminQueries.getUsers(usernames)
        );

        const r = data.users.map((user, index) => ({
          ...user,
          ...transformCognitoData(cognitoResult[index]),
        }));

        this.totalUsers = data.users_aggregate.aggregate.count;
        this.users = r;
        this.isPostProcessingUsers = false;
      },
    },
  },
  data() {
    return {
      users: [],
      totalUsers: 0,
      perPage: this.$options.PER_PAGE_OPTIONS[0],
      page: 1,
      isPostProcessingUsers: false,
    };
  },
  computed: {
    ...mapState('user', ['user']),
    ...mapGetters('user', ['isAdmin', 'isCompanyAdmin']),
  },
  methods: {
    setPage(page) {
      const query = this.$apollo.queries.users;

      const more = query.fetchMore({
        variables: {
          offset: (page - 1) * this.perPage,
          limit: this.perPage,
        },
      });

      if (more) {
        more.then(() => {
          this.page = page;
        });
      }
    },
    setPerPage(value) {
      this.perPage = value;
      this.setPage(1);
    },
    async disableUser(username) {
      this.isPostProcessingUsers = true;
      const api = this.isCompanyAdmin ? companyAdminQueries : adminQueries;
      await api.disableUser(username);
      await this.$apollo.mutate({
        mutation: gql`mutation ($userId: String!) {
          update_users_by_pk(_set: {suspended: true}, pk_columns: {id: $userId}) {
            id
          }
        }`,
        variables: {
          userId: username,
        },
      });
      this.isPostProcessingUsers = false;
      this.setPage(this.page);
    },
  },
};
</script>

<style lang="scss" module>
.page {
  flex-grow: 1;
  padding-top: map-get($page-vertical-paddings, 'small');
  padding-bottom: map-get($page-vertical-paddings, 'small');
}
</style>
